import { anyCharExcept, char, choice, digits, many, possibly, sequenceOf } from "arcsecond";

// PEG: "[" [0-9]+ "]"
const $citation = sequenceOf([
  char("["),
  digits,
  char("]"),
]).map(([_, num, __]) => Number(num));

// PEG: "[" [0-9]+ ("," [0-9]+)* ","* "]"
const $citationGroup = sequenceOf([
  char("["),
  sequenceOf([
    digits,
    many(
      sequenceOf([
        char(","),
        digits,
      ]).map(([_, num]) => num)
    )
  ]).map(([first, rest]) => [first, ...rest].map(Number)),
  possibly(char(",")),
  char("]"),
]).map(([_1, nums, _2, _3]) => nums)

// PEG: ($citation / $citationGroup / . !($citation / $citationGroup))*
const $textWithCitation = many(
  choice([
    $citation.map(num => ({
      type: /**@type{const}*/("citation"),
      content: num,
    })),
    $citationGroup.map(nums => ({
      type: /**@type{const}*/("citation-group"),
      content: nums,
    })),
    many(
      anyCharExcept(
        choice([
          $citation,
          $citationGroup,
        ])
      )
    ).map(chars => ({
      type: /**@type{const}*/("text"),
      content: chars.join(""),
    })),
  ])
)

/**
 * @param {number} num
 * @param {Citation[]} citations
 */
function DEFAULT_CITATION_FORMATTER(num, citations) {
  // Citations are 1-indexed in the text (e.g., [1], [2], [3])
  // But the citations array is 0-indexed
  // So citation [1] in text refers to citations[0] in the array
  const citation = citations[num - 1]
  if(citation == null) return ""
  return `<cite data-citation-number="${num}"><a href="${citation.url}">${num}</a></cite>`
}


/**
 * @param {string} text
 * @param {Citation[]} citations
 * @param {function(string, Citation[]): string} [formatCitation]
 * @returns {string}
 */
function formatTextWithCitation(text, citations, formatCitation = DEFAULT_CITATION_FORMATTER) {
  const result = $textWithCitation.run(text);
  if(result.isError) throw new Error(result.error)
    
  const nodes = result.result
  return nodes.map(node => {
    if (node.type === "citation") {
      return formatCitation(node.content, citations);
    }
    if(node.type === "citation-group") {
      return node.content.map(num => formatCitation(num, citations)).join("")
    }
    if(node.type === "text") {
      return node.content
    }

    throw new Error(`Unknown node type: ${node?.type}`)
  }).join("");
}

/**
 * @typedef {Object} Citation
 * @property {string} title
 * @property {string} url
 */


export {
  formatTextWithCitation,
}